import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.practice');
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { AudioEngine, PitchEvent, midiToNoteName, midiToHz } from "src/libs/portal/season/audio";

type PracticeMode = 'select' | 'single' | 'sequence' | 'free';
type PracticeState = 'ready' | 'listening' | 'success' | 'fail' | 'complete';

@Component({
    selector: 'wiz-page-practice',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.practice/view.scss */
:host {
  display: block;
  height: 100%;
}`],
})
export class PagePracticeComponent implements OnInit, OnDestroy {
    constructor(@Inject( Service) public service: Service) { }

    public engine: AudioEngine | null = null;
    public mode: PracticeMode = 'select';
    public state: PracticeState = 'ready';
    public errorMessage: string = '';
    public starting: boolean = false;

    // 실시간 피치
    public currentHz: number = 0;
    public currentMidi: number = 0;
    public currentNote: string = '';
    public currentCents: number = 0;
    public currentRms: number = 0;
    public isInTarget: boolean = false;
    public holdProgress: number = 0;
    public isVoice: boolean = false;

    // 연습 상태
    public targetMidi: number = 0;
    public targetNote: string = '';
    public score: number = 0;
    public round: number = 0;
    public totalRounds: number = 10;
    public results: any[] = [];

    // 시퀀스 모드
    public sequence: number[] = [];
    public sequenceIndex: number = 0;

    // 피아노 건반
    public pianoKeys: any[] = [];
    public pianoRangeLow: number = 48;
    public pianoRangeHigh: number = 72;

    // 난이도
    public difficulty: string = 'easy';
    public difficultyRanges: any = {
        easy: { low: 55, high: 65 },    // G3~F4
        medium: { low: 48, high: 72 },  // C3~C5
        hard: { low: 40, high: 80 }     // E2~G#5
    };

    public async ngOnInit() {
        await this.service.init();
        this.buildPianoKeys();
        await this.service.render();
    }

    public ngOnDestroy() { this.cleanup(); }

    public buildPianoKeys() {
        this.pianoKeys = [];
        for (let midi = this.pianoRangeLow; midi <= this.pianoRangeHigh; midi++) {
            const noteNum = midi % 12;
            const isBlack = [1, 3, 6, 8, 10].includes(noteNum);
            this.pianoKeys.push({ midi, note: midiToNoteName(midi), isBlack, isC: noteNum === 0 });
        }
    }

    public getPianoKeyClass(key: any): string {
        const isCurrent = this.currentMidi === key.midi && this.currentHz > 0;
        const isTarget = this.targetMidi === key.midi;
        if (key.isBlack) {
            if (isCurrent && isTarget) return 'bg-green-500';
            if (isCurrent) return 'bg-blue-500';
            if (isTarget) return 'bg-indigo-400';
            return 'bg-gray-800';
        }
        if (isCurrent && isTarget) return 'bg-green-300 border-green-400';
        if (isCurrent) return 'bg-blue-200 border-blue-300';
        if (isTarget) return 'bg-indigo-100 border-indigo-300';
        return 'bg-white border-gray-300';
    }

    /* ── 모드 선택 ── */

    public async selectMode(mode: PracticeMode) {
        if (this.starting) return;
        this.mode = mode;
        this.score = 0;
        this.round = 0;
        this.results = [];
        this.errorMessage = '';

        if (mode === 'sequence') {
            this.generateSequence();
        } else {
            this.totalRounds = 10;
            this.sequence = [];
            this.sequenceIndex = 0;
        }

        await this.initEngine();
    }

    /* ── 엔진 초기화 ── */

    private async initEngine() {
        this.cleanup();
        this.starting = true;
        this.errorMessage = '';
        await this.service.render();

        this.engine = new AudioEngine({
            bufferSize: 2048,
            holdDuration: 1000,
            targetTolerance: 1.0,
            minConfidence: 0.65,
            noiseSuppression: true,
            renderThrottle: 50
        });

        try {
            const support = await this.engine.checkBrowserSupport();
            if (!support.supported) {
                this.errorMessage = '이 브라우저에서는 마이크를 사용할 수 없습니다. HTTPS 환경에서 Chrome 또는 Safari를 사용해주세요.';
                this.cleanup();
                return;
            }

            const granted = await this.engine.requestMicrophone();
            if (!granted) {
                this.errorMessage = '마이크 권한이 필요합니다. 브라우저 주소창의 권한 설정에서 마이크를 허용한 뒤 다시 시도해주세요.';
                this.cleanup();
                return;
            }

            await this.engine.measureEnvironment(1000);

            this.engine.onPitch((e: PitchEvent) => {
                this.currentHz = Math.round(e.hz);
                this.currentMidi = e.midi;
                this.currentNote = e.noteName;
                this.currentCents = e.cents;
                this.currentRms = Math.min(100, e.rms * 500);
                this.isInTarget = e.isInTarget;
                this.holdProgress = e.holdProgress;
                this.isVoice = e.isVoice;
                this.service.render();
            });

            this.engine.onNoteHeld((midi: number) => {
                if (midi === this.targetMidi) this.onSuccess();
            });

            await this.engine.start();

            if (this.mode === 'free') {
                this.state = 'listening';
            } else {
                this.nextTarget();
            }
        } catch (e) {
            this.errorMessage = '마이크를 시작하는 중 문제가 발생했습니다. 권한과 입력 장치를 확인한 뒤 다시 시도해주세요.';
            this.cleanup();
        } finally {
            this.starting = false;
            await this.service.render();
        }
    }

    /* ── 다음 목표 음 ── */

    private nextTarget() {
        if (this.mode === 'single') {
            const range = this.difficultyRanges[this.difficulty];
            this.targetMidi = Math.floor(Math.random() * (range.high - range.low + 1)) + range.low;
            // 반음만 포함된 키는 쉬움에서 제외
            if (this.difficulty === 'easy') {
                const whiteKeys = [0, 2, 4, 5, 7, 9, 11];
                while (!whiteKeys.includes(this.targetMidi % 12)) {
                    this.targetMidi = Math.floor(Math.random() * (range.high - range.low + 1)) + range.low;
                }
            }
        } else if (this.mode === 'sequence') {
            if (this.sequenceIndex >= this.sequence.length) {
                this.finishPractice();
                return;
            }
            this.targetMidi = this.sequence[this.sequenceIndex];
        }

        this.targetNote = midiToNoteName(this.targetMidi);
        this.state = 'listening';
        this.holdProgress = 0;
        if (this.engine) this.engine.setTarget(this.targetMidi);

        // 피아노 범위 업데이트
        this.pianoRangeLow = Math.max(24, this.targetMidi - 12);
        this.pianoRangeHigh = Math.min(96, this.targetMidi + 12);
        this.buildPianoKeys();

        this.round++;
        this.service.render();
    }

    private generateSequence() {
        const range = this.difficultyRanges[this.difficulty];
        this.sequence = [];
        // 도레미파솔라시도 + 랜덤 음
        const scale = [0, 2, 4, 5, 7, 9, 11, 12];
        const base = Math.max(range.low, 60);
        for (const s of scale) {
            const midi = base + s;
            if (midi <= range.high) this.sequence.push(midi);
        }
        this.totalRounds = this.sequence.length;
        this.sequenceIndex = 0;
    }

    /* ── 성공 처리 ── */

    private async onSuccess() {
        this.state = 'success';
        this.score++;
        this.results.push({ midi: this.targetMidi, note: this.targetNote, success: true });
        await this.service.render();

        setTimeout(async () => {
            if (this.mode === 'sequence') {
                this.sequenceIndex++;
                if (this.sequenceIndex >= this.sequence.length) {
                    this.finishPractice();
                    return;
                }
            }
            if (this.round >= this.totalRounds && this.mode === 'single') {
                this.finishPractice();
                return;
            }
            this.nextTarget();
        }, 600);
    }

    public async skipTarget() {
        this.results.push({ midi: this.targetMidi, note: this.targetNote, success: false });
        if (this.mode === 'sequence') this.sequenceIndex++;
        if (this.round >= this.totalRounds || (this.mode === 'sequence' && this.sequenceIndex >= this.sequence.length)) {
            this.finishPractice();
            return;
        }
        this.nextTarget();
    }

    private async finishPractice() {
        this.state = 'complete';
        if (this.engine) this.engine.stop();

        // 결과 저장
        try {
            const saveData = {
                test_mode: 'practice',
                quality_score: Math.round((this.score / Math.max(1, this.results.length)) * 100),
                environment_score: 0,
                lowest_stable_note: 0, highest_stable_note: 0,
                comfortable_low: 0, comfortable_high: 0,
                spoken_range_low: 0, spoken_range_high: 0,
                voice_type_estimate: ''
            };
            await wiz.call("save_result", saveData);
        } catch (e) { }

        await this.service.render();
    }

    /* ── 난이도 변경 ── */

    public setDifficulty(d: string) {
        this.difficulty = d;
        this.service.render();
    }

    /* ── 유틸 ── */

    public getCentsText(): string {
        if (!this.currentHz || this.currentCents === 0) return '';
        return this.currentCents > 0 ? `+${this.currentCents}¢` : `${this.currentCents}¢`;
    }

    public getStaffY(midi: number): number {
        const notePositions: any = { 0: 0, 1: 0, 2: 1, 3: 1, 4: 2, 5: 3, 6: 3, 7: 4, 8: 4, 9: 5, 10: 5, 11: 6 };
        const noteNum = midi % 12;
        const octave = Math.floor(midi / 12) - 1;
        const baseLine = (octave - 4) * 7 + notePositions[noteNum];
        return 60 - baseLine * 5;
    }

    public isSharp(midi: number): boolean {
        return [1, 3, 6, 8, 10].includes(midi % 12);
    }

    public getNoteName(midi: number): string { return midiToNoteName(midi); }
    public noteLabel(note: string): string { return note.replace(/[0-9]/g, ''); }

    public goHome() { this.cleanup(); this.service.href('/'); }
    public async retryMicrophone() {
        if (this.mode === 'select') return;
        await this.initEngine();
    }

    public restart() {
        this.cleanup();
        this.mode = 'select';
        this.state = 'ready';
        this.errorMessage = '';
        this.starting = false;
        this.currentHz = 0;
        this.currentMidi = 0;
        this.currentNote = '';
        this.holdProgress = 0;
        this.service.render();
    }

    private cleanup() {
        if (this.engine) { this.engine.destroy(); this.engine = null; }
    }
}

export default PagePracticeComponent;